/**
 * CartManager - Handles cart functionality, API calls, and cart UI rendering
 * Replicates the Unity cart system from designer-unity
 */

import { API_BASE_URL, assetUrl } from "../config.js";
import { openExternal, apiFetch } from '../platform/index.js';
import { fetchApiJson } from '../core/ApiData.js';
import { appendImage } from '../utils/imagePool.js';
import { collectOrderEntries, formatOrderLink } from './orderEntries.js';

// The size the cart shots are baked at, so the img reserves the same box.
const CART_THUMBNAIL_W = 128;
const CART_THUMBNAIL_H = 96;

// The endpoint prices one piece and localizes each row ("50.634 ден", "1.518,00 €",
// "£1,120.00"), so a grouped row rewrites the number in place and leaves the rest alone.
const PRICE_NUMBER_RE = /\d[\d.,'\s]*\d|\d/;

/** Locate and read the number inside a formatted price. */
function parsePriceNumber(text) {
  const match = PRICE_NUMBER_RE.exec(text);
  if (!match) return null;

  const token = match[0];
  const lastDot = token.lastIndexOf('.');
  const lastComma = token.lastIndexOf(',');
  const cut = Math.max(lastDot, lastComma);

  const tail = cut === -1 ? '' : token.slice(cut + 1);
  // One separator kind with exactly three digits behind it groups thousands ("50.634"); anything else is the decimal mark.
  const grouping = tail.length === 3 && (lastDot === -1 || lastComma === -1);
  const decimalSep = cut !== -1 && !grouping ? token[cut] : '';

  const head = decimalSep ? token.slice(0, cut) : token;
  const intDigits = head.replace(/\D/g, '');
  const fracDigits = decimalSep ? tail.replace(/\D/g, '') : '';
  const value = Number(fracDigits ? `${intDigits}.${fracDigits}` : intDigits);
  if (!Number.isFinite(value)) return null;

  const groupMatch = head.match(/[.,'\s]/);
  return { value, decimalSep, decimals: fracDigits.length, groupSep: groupMatch ? groupMatch[0] : '' };
}

// Thousands separator for a row that only crosses a thousand once multiplied; `hint` is
// another price the same backend formatted, since a decimal mark alone implies its complement.
function inferGroupSeparator(parsed, hint) {
  if (parsed.groupSep) return parsed.groupSep;

  const hinted = typeof hint === 'string' ? parsePriceNumber(hint) : null;
  if (hinted?.groupSep && hinted.groupSep !== parsed.decimalSep) return hinted.groupSep;

  if (parsed.decimalSep === '.') return ',';
  if (parsed.decimalSep === ',') return '.';
  return '';
}

/** Multiply a formatted price by a quantity, keeping the backend's formatting. Returns the text untouched when there is nothing to scale. */
export function scalePriceText(text, quantity, formatHint) {
  if (typeof text !== 'string' || !text) return text;
  const qty = Math.round(Number(quantity));
  if (!Number.isFinite(qty) || qty <= 1) return text;

  const parsed = parsePriceNumber(text);
  if (!parsed) return text;

  const [intPart, fracPart] = (parsed.value * qty).toFixed(parsed.decimals).split('.');
  const groupSep = inferGroupSeparator(parsed, formatHint);
  const grouped = groupSep ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, groupSep) : intPart;
  const scaled = fracPart ? grouped + parsed.decimalSep + fracPart : grouped;
  return text.replace(PRICE_NUMBER_RE, () => scaled);
}

// Data models matching Unity's data structure
class CartSummary {
  constructor(data) {
    this.oldStyle = data.oldStyle || false;
    this.error = data.error || null;
    this.translations = new CartTranslations(data.translations || {});
    this.summaryItems = (data.summaryItems || []).map(item => new CartItem(item));
    this.totalAmount = data.totalAmount || "";
  }
}

class CartTranslations {
  constructor(data) {
    this.orderSummary = data.orderSummary || "Order Summary";
    this.item = data.item || "Item";
    this.price = data.price || "Price";
    this.total = data.total || "Total";
    this.buttonText = data.buttonText || "Open in Browser";
  }
}

class CartItem {
  constructor(data) {
    this.itemText = data.itemText || []; // Array of strings [name, ...configuration details]
    this.itemImage = data.itemImage || "";
    this.price = data.price || "";
    this.discountedPrice = data.discountedPrice || null;
    this.discountName = data.discountName || null;
    this.quantity = parseInt(data.quantity) || 1;
    // The order entry this row came from, filled in by fetchCartSummary.
    this.productCode = data.productCode || null;
    this.fabricCode = data.fabricCode || null;
    this.boardColor = data.boardColor || null;
  }

  get name() {
    return this.itemText.length > 0 ? this.itemText[0] : "";
  }

  get configDetails() {
    return this.itemText.slice(1); // All items after name are config details
  }

  get hasDiscount() {
    return this.discountedPrice !== null && this.discountedPrice !== "";
  }
}

export class CartManager {
  constructor(app) {
    this.app = app;
    this.currentSummary = null;
    this.isLoading = false;

    // API configuration - matches Unity's setup
    this.apiBaseUrl = API_BASE_URL;
    this.priceEndpoint = "/api/app-summary/";

    // Country and feature flags
    this.countryCode = null;
    this.language = null;
    this.countryFeatures = null;
    this.isCartAvailable = false;
  }

  /**
   * Initialize cart manager
   */
  async init() {
    this.setupCartModal();
    this.setupEventListeners();

    // Fetch country info and check if cart is available
    await this.initializeCountryFeatures();
  }

  /**
   * Initialize country features - fetch country info and determine cart availability
   */
  async initializeCountryFeatures() {
    try {
      // Get country/language from LocalizationManager
      const localizationManager = this.app.getLocalizationManager();
      this.countryCode = localizationManager?.getCountry() || 'GLOBAL';
      this.language = localizationManager?.getLanguage() || 'en';

      // Fetch country features for cart availability
      this.countryFeatures = await this.fetchCountryFeatures();

      // Check if cart should be available for this country
      this.isCartAvailable = this.checkCartAvailability();

      // Notify UIManager about cart availability
      const uiManager = this.app.getUIManager();
      if (uiManager) {
        uiManager.updateCartButtonVisibility(this.isCartAvailable);
      }

    } catch (error) {
      console.error('Failed to initialize country features:', error);
      // Default to cart available if we can't determine
      this.isCartAvailable = true;
      const uiManager = this.app.getUIManager();
      if (uiManager) {
        uiManager.updateCartButtonVisibility(true);
      }
    }
  }

  /**
   * Fetch country features from backend
   * @returns {Promise<Object>}
   */
  async fetchCountryFeatures() {
    const data = await fetchApiJson('country-features', { snapshot: 'country-features.json' });
    if (!data) throw new Error('No country features available');
    return data;
  }

  /**
   * Check if cart should be available based on country features
   * @returns {boolean}
   */
  checkCartAvailability() {
    if (!this.countryCode || !this.countryFeatures) {
      return true; // Default to available
    }

    const features = this.countryFeatures[this.countryCode];
    if (!features) {
      return true; // Country not found, default to available
    }

    // Cart is available if priceAndQuestion === "true" OR webShop === "true"
    const hasPriceAndQuestion = features.priceAndQuestion === "true";
    const hasWebShop = features.webShop === "true";

    return hasPriceAndQuestion || hasWebShop;
  }

  /**
   * Setup cart modal HTML structure
   */
  setupCartModal() {
    const modalHTML = `
      <div id="cart-modal" class="settings-modal cart-modal hidden">
        <div class="settings-modal-content cart-modal-content">
          <div class="settings-header">
            <h3 class="settings-title" id="cart-title">Order Summary</h3>
            <button id="close-cart-btn" class="settings-close-btn" title="Close">×</button>
          </div>

          <div class="settings-body" id="cart-body">
            <!-- Cart items will be dynamically rendered here -->
            <div class="cart-loading" id="cart-loading">
              <div class="cart-loading-spinner"></div>
              <p data-i18n="designerCartLoading">${this.app.getLocalizationManager()?.t('designerCartLoading', 'Loading cart...') ?? 'Loading cart...'}</p>
            </div>

            <div class="cart-content" id="cart-content" style="display: none;">
              <!-- Column headers -->
              <div class="cart-table-header">
                <div class="cart-header-item" id="cart-header-item">Item</div>
                <div class="cart-header-price" id="cart-header-price">Price</div>
              </div>

              <!-- Items container -->
              <div class="cart-items" id="cart-items">
                <!-- Items will be rendered here -->
              </div>

              <!-- Total section -->
              <div class="cart-total">
                <div class="cart-total-label" id="cart-total-label">Total (without Shipping & Tax)</div>
                <div class="cart-total-amount" id="cart-total-amount">$0.00</div>
              </div>
            </div>

            <div class="cart-error" id="cart-error" style="display: none;">
              <p id="cart-error-message">Failed to load cart data</p>
            </div>
          </div>

          <div class="cart-footer">
            <button id="cart-open-browser-btn" class="cart-btn cart-primary-btn">
              Open in Browser
            </button>
          </div>
        </div>
      </div>
    `;

    // Insert modal into document
    document.body.insertAdjacentHTML('beforeend', modalHTML);
  }

  /**
   * Setup event listeners for cart modal
   */
  setupEventListeners() {
    const closeBtn = document.getElementById('close-cart-btn');
    const openBrowserBtn = document.getElementById('cart-open-browser-btn');
    const cartModal = document.getElementById('cart-modal');

    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closeCart());
    }

    if (openBrowserBtn) {
      openBrowserBtn.addEventListener('click', () => this.openOrderInBrowser());
    }

    // Close modal when clicking outside
    if (cartModal) {
      cartModal.addEventListener('click', (e) => {
        if (e.target === cartModal) {
          this.closeCart();
        }
      });
    }
  }

  /**
   * Fetch cart summary from API
   * @returns {Promise<CartSummary>}
   */
  /** @see collectOrderEntries */
  getOrderEntries() {
    return collectOrderEntries(this.app);
  }

  async fetchCartSummary() {
    // Entries too, not just the link: the endpoint's names come back localized, its own entry doesn't.
    const orderEntries = this.getOrderEntries();
    const orderLink = formatOrderLink(orderEntries);
    if (!orderLink) {
      throw new Error('Could not generate order link');
    }

    // Get current language code (default to 'en')
    const languageCode = this.getLanguageCode();

    // Build API URL
    // Format: http://localhost:8090/api/app-summary/?product1+faFabric1&product2+faFabric2&lang=en&country=MK
    let url = `${this.apiBaseUrl}${this.priceEndpoint}?${orderLink}&lang=${languageCode}`;

    // Add country if it's a specific country (not GLOBAL)
    if (this.countryCode && this.countryCode !== 'GLOBAL') {
      url += `&country=${this.countryCode}`;
    }

    const response = await apiFetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      timeout: 10000, // 10 second timeout
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const text = await response.text();
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (!jsonMatch) throw new Error('Cart summary response carried no JSON');
    const data = JSON.parse(jsonMatch[0]);

    const summary = new CartSummary(data);

    // The endpoint emits one summary item per entry, in order, so entry i describes item i.
    if (orderEntries?.length === summary.summaryItems.length) {
      summary.summaryItems.forEach((item, i) => {
        item.productCode = orderEntries[i].productCode;
        item.fabricCode = orderEntries[i].fabricCode;
        item.boardColor = orderEntries[i].boardColor;
      });
    }

    return summary;
  }

  /**
   * Get current language code
   * @returns {string}
   */
  getLanguageCode() {
    // Use language from backend if available
    if (this.language) {
      return this.language;
    }

    // Fallback to browser language
    const browserLang = navigator.language || navigator.userLanguage;
    return browserLang.split('-')[0] || 'en';
  }

  /**
   * Open cart modal and load data
   */
  async openCart() {
    const modal = document.getElementById('cart-modal');
    if (!modal) return;

    // Show modal
    modal.classList.remove('hidden');

    // Show loading state
    this.showLoadingState();

    try {
      // Fetch cart data
      const summary = await this.fetchCartSummary();

      // Check for errors from API
      if (summary.error) {
        this.showErrorState(summary.error);
        return;
      }

      // Handle old style (direct browser redirect)
      if (summary.oldStyle) {
        this.closeCart();
        this.openOrderInBrowser();
        return;
      }

      this.currentSummary = summary;

      // Render cart
      this.renderCart(summary);

      // Show content
      this.showContentState();

    } catch (error) {
      console.error('Failed to fetch cart data:', error);
      this.showErrorState(error.message);
    }
  }

  /**
   * Close cart modal
   */
  closeCart() {
    const modal = document.getElementById('cart-modal');
    if (modal) {
      modal.classList.add('hidden');
    }
  }

  /**
   * Show loading state
   */
  showLoadingState() {
    const loading = document.getElementById('cart-loading');
    const content = document.getElementById('cart-content');
    const error = document.getElementById('cart-error');

    if (loading) loading.style.display = 'flex';
    if (content) content.style.display = 'none';
    if (error) error.style.display = 'none';
  }

  /**
   * Show content state
   */
  showContentState() {
    const loading = document.getElementById('cart-loading');
    const content = document.getElementById('cart-content');
    const error = document.getElementById('cart-error');

    if (loading) loading.style.display = 'none';
    if (content) content.style.display = 'block';
    if (error) error.style.display = 'none';
  }

  /**
   * Show error state
   */
  showErrorState(message) {
    const loading = document.getElementById('cart-loading');
    const content = document.getElementById('cart-content');
    const error = document.getElementById('cart-error');
    const errorMessage = document.getElementById('cart-error-message');

    if (loading) loading.style.display = 'none';
    if (content) content.style.display = 'none';
    if (error) error.style.display = 'flex';
    if (errorMessage) errorMessage.textContent = message;
  }

  /**
   * Group identical cart items together and sum their quantities
   * @param {CartItem[]} items
   * @returns {CartItem[]}
   */
  groupCartItems(items) {
    const grouped = new Map();

    for (const item of items) {
      // Create a unique key based on item configuration
      const key = JSON.stringify({
        productCode: item.productCode,
        fabricCode: item.fabricCode,
        boardColor: item.boardColor,
        name: item.name,
        config: item.configDetails,
        price: item.price,
        discountedPrice: item.discountedPrice,
        image: item.itemImage
      });

      if (grouped.has(key)) {
        // Item exists, add to quantity
        const existingItem = grouped.get(key);
        const existingQty = parseInt(existingItem.quantity) || 1;
        const newQty = parseInt(item.quantity) || 1;
        existingItem.quantity = existingQty + newQty;
      } else {
        // New item, create a new CartItem instance to preserve methods/getters
        const groupedItem = new CartItem({
          itemText: item.itemText,
          itemImage: item.itemImage,
          price: item.price,
          discountedPrice: item.discountedPrice,
          discountName: item.discountName,
          quantity: item.quantity,
          productCode: item.productCode,
          fabricCode: item.fabricCode,
          boardColor: item.boardColor
        });
        grouped.set(key, groupedItem);
      }
    }

    return Array.from(grouped.values());
  }

  /**
   * Render cart summary
   * @param {CartSummary} summary
   */
  renderCart(summary) {
    // Update header labels with translations
    const titleEl = document.getElementById('cart-title');
    const headerItemEl = document.getElementById('cart-header-item');
    const headerPriceEl = document.getElementById('cart-header-price');
    const totalLabelEl = document.getElementById('cart-total-label');
    const totalAmountEl = document.getElementById('cart-total-amount');
    const browserBtnEl = document.getElementById('cart-open-browser-btn');

    if (titleEl) titleEl.textContent = summary.translations.orderSummary;
    if (headerItemEl) headerItemEl.textContent = summary.translations.item;
    if (headerPriceEl) headerPriceEl.textContent = summary.translations.price;
    if (totalLabelEl) totalLabelEl.textContent = summary.translations.total;
    if (totalAmountEl) totalAmountEl.textContent = summary.totalAmount;
    if (browserBtnEl) browserBtnEl.textContent = summary.translations.buttonText;

    // Group identical items together
    const groupedItems = this.groupCartItems(summary.summaryItems);

    // Render items
    const itemsContainer = document.getElementById('cart-items');
    if (!itemsContainer) return;

    itemsContainer.innerHTML = '';

    for (const item of groupedItems) {
      itemsContainer.appendChild(this.createCartItemElement(item));
    }
  }

  /**
   * Create cart item element
   * @param {CartItem} item
   * @returns {HTMLElement}
   */
  createCartItemElement(item) {
    const itemRow = document.createElement('div');
    itemRow.className = 'cart-item-row';

    // Item info section (image + details)
    const itemInfo = document.createElement('div');
    itemInfo.className = 'cart-item-info';

    // Item image - always use local assets (the API's itemImage is cross-origin)
    const itemImage = document.createElement('div');
    itemImage.className = 'cart-item-image';

    this.appendItemThumbnail(item, itemImage);

    // Item details (name + configuration)
    const itemDetails = document.createElement('div');
    itemDetails.className = 'cart-item-details';

    const itemName = document.createElement('div');
    itemName.className = 'cart-item-name';
    itemName.textContent = item.name;

    const itemConfig = document.createElement('div');
    itemConfig.className = 'cart-item-config';

    // Add each configuration detail on a new line
    item.configDetails.forEach(detail => {
      const detailLine = document.createElement('div');
      detailLine.className = 'cart-item-config-line';
      detailLine.textContent = detail;
      itemConfig.appendChild(detailLine);
    });

    itemDetails.appendChild(itemName);
    itemDetails.appendChild(itemConfig);

    itemInfo.appendChild(itemImage);
    itemInfo.appendChild(itemDetails);

    // Price section
    const itemPriceSection = document.createElement('div');
    itemPriceSection.className = 'cart-item-price-section';

    // A grouped row prices the whole group; the unit price moves to the quantity line.
    const scaled = (text) => scalePriceText(text, item.quantity, this.currentSummary?.totalAmount);

    const regularPrice = document.createElement('div');
    regularPrice.className = `cart-item-price-regular${item.hasDiscount ? ' strikethrough' : ''}`;
    regularPrice.textContent = scaled(item.price);
    itemPriceSection.appendChild(regularPrice);

    if (item.hasDiscount) {
      const discountedPrice = document.createElement('div');
      discountedPrice.className = 'cart-item-price-discounted';
      discountedPrice.textContent = scaled(item.discountedPrice);
      itemPriceSection.appendChild(discountedPrice);
    }

    // Only show quantity when more than 1
    if (item.quantity > 1) {
      const pcsText = this.app.getLocalizationManager()?.t('pcs', 'pcs') || 'pcs';
      const unitPrice = item.hasDiscount ? item.discountedPrice : item.price;
      const quantity = document.createElement('div');
      quantity.className = 'cart-item-quantity';
      quantity.textContent = unitPrice
        ? `${item.quantity} ${pcsText} × ${unitPrice}`
        : `${item.quantity} ${pcsText}`;
      itemPriceSection.appendChild(quantity);
    }

    itemRow.appendChild(itemInfo);
    itemRow.appendChild(itemPriceSection);

    return itemRow;
  }

  /**
   * Put the row's baked product shot in `holder`, falling back through thumbnailSources().
   * Pooled: 22 element codes have no cart shot at all, so an unpooled row walks two 404s on
   * every cart open — 404s are not cached.
   * @param {CartItem} item
   * @param {HTMLElement} holder
   */
  appendItemThumbnail(item, holder) {
    const sources = this.thumbnailSources(item);
    if (!sources.length) {
      holder.innerHTML = '<div class="cart-item-image-placeholder"></div>';
      return;
    }
    const img = appendImage(holder, sources, {
      alt: item.name,
      className: 'cart-item-img',
      onExhausted: () => { holder.innerHTML = '<div class="cart-item-image-placeholder"></div>'; },
    });
    img.width = CART_THUMBNAIL_W;
    img.height = CART_THUMBNAIL_H;
  }

  /**
   * Shot URLs for a row, most specific first: `cart/<code>[_c<FABRIC>][_bl|_bd]`, plain, hero.
   * @param {CartItem} item
   * @returns {string[]}
   */
  thumbnailSources(item) {
    const furnitureData = this.resolveCatalogItem(item);
    const urlCode = furnitureData?.urlCode;
    if (!urlCode) return [];

    const zones = this.app.getFurnitureManager().getMaterialZones(furnitureData);
    const parts = [urlCode];
    // Only ask for an axis the item actually has — guessing either way just costs a 404.
    if (item.fabricCode && zones.includes('fabric')) parts.push(`c${item.fabricCode}`);
    if (item.boardColor && zones.includes('wood')) parts.push(item.boardColor === 'dark' ? 'bd' : 'bl');

    const sources = [assetUrl(`thumbnails/cart/${parts.join('_')}.webp`)];
    if (parts.length > 1) sources.push(assetUrl(`thumbnails/cart/${urlCode}.webp`));
    sources.push(assetUrl(`thumbnails/elements/${urlCode}.webp`));
    return sources;
  }

  /**
   * Catalog entry behind a row: by product code, else the itemText scan.
   * @param {CartItem} item
   * @returns {Object|null}
   */
  resolveCatalogItem(item) {
    const furnitureManager = this.app.getFurnitureManager();
    const code = item.productCode;
    if (code && furnitureManager) {
      const direct = furnitureManager.findCatalogItemByUrlCode(code);
      if (direct) return direct;
      // A variant can override the code (clooods-co orders as clooods-cx); shots are per item.
      const collections = furnitureManager.getFurnitureCollections() || {};
      for (const key of Object.keys(collections)) {
        for (const f of collections[key].items || []) {
          if ((f.properties?.variants || []).some((v) => v.urlCode === code)) return f;
        }
      }
    }
    return this.findFurnitureDataForCartItem(item)?.furnitureData || null;
  }

  /**
   * Resolve a cart item to a catalog entry by scanning every line in
   * `itemText` (name + config details). For each candidate string, tries:
   *   1. exact case-insensitive `name` match (item, then variant)
   *   2. normalized match (strip whitespace + dashes, lowercased) against
   *      `name` and `urlCode` — handles "BonBon 3 T" ↔ "bonbon3-t",
   *      "Choice P14 R" ↔ "choice-p14-r", and variant-level urlCode overrides
   *      like clooods-co "Corner M L" → "clooods-cx"
   *   3. legacy "<Collection> Element <CODE>" → "collection-code" heuristic
   * Variant hits win over item-level hits (more specific). Returns
   * `{ furnitureData, variantIndex }` where variantIndex is the matched
   * variant when the hit came from a variant override, or null to mean
   * "use the scene element's variant".
   * @param {CartItem} item
   * @returns {{ furnitureData: Object, variantIndex: number|null }|null}
   */
  findFurnitureDataForCartItem(item) {
    const furnitureManager = this.app.getFurnitureManager();
    if (!furnitureManager) return null;
    const collections = furnitureManager.getFurnitureCollections();

    const candidates = [item?.name || '', ...(item?.configDetails || [])]
      .filter((s) => typeof s === 'string' && s.length > 0);

    let exact = null;
    let normalized = null;
    let variantHit = null; // { furnitureData, variantIndex }

    for (const candidate of candidates) {
      const lower = candidate.toLowerCase();
      const norm = lower.replace(/[\s-]+/g, '');

      for (const key of Object.keys(collections)) {
        for (const f of collections[key].items || []) {
          const fname = (f.name || '').toLowerCase();
          if (!exact && fname === lower) exact = f;
          if (!normalized && (fname.replace(/[\s-]+/g, '') === norm ||
                              (f.urlCode || '').replace(/-/g, '') === norm)) {
            normalized = f;
          }
          if (!variantHit) {
            const variants = f.properties?.variants;
            if (variants) {
              for (let i = 0; i < variants.length; i++) {
                const v = variants[i];
                const vname = (v.name || '').toLowerCase();
                const cartName = (v.cartName || '').toLowerCase();
                if (vname === lower ||
                    vname.replace(/[\s-]+/g, '') === norm ||
                    (v.urlCode || '').replace(/-/g, '') === norm ||
                    cartName === lower ||
                    cartName.replace(/[\s-]+/g, '') === norm) {
                  variantHit = { furnitureData: f, variantIndex: i };
                  break;
                }
              }
            }
          }
        }
        if (variantHit) break;
      }

      if (variantHit) break;
    }

    // Variant hits are more specific than item-level hits; prefer them.
    if (variantHit) return variantHit;
    if (exact) return { furnitureData: exact, variantIndex: null };
    if (normalized) return { furnitureData: normalized, variantIndex: null };

    // Legacy: "<Collection> Element <CODE>" → "collection-code"
    for (const candidate of candidates) {
      const m = candidate.match(/^(\w+)\s+Element\s+(\w+)/i);
      if (!m) continue;
      const urlCode = `${m[1].toLowerCase()}-${m[2].toLowerCase()}`;
      for (const key of Object.keys(collections)) {
        const f = (collections[key].items || []).find(f => f.urlCode === urlCode);
        if (f) return { furnitureData: f, variantIndex: null };
      }
    }

    console.warn('[cart] Unable to resolve catalog entry for item:', {
      name: item?.name,
      configDetails: item?.configDetails,
    });
    return null;
  }

  /**
   * Open order in browser
   * Redirects to /app endpoint which processes the order and redirects to checkout or selection
   */
  openOrderInBrowser() {
    const orderLink = formatOrderLink(this.getOrderEntries());
    if (!orderLink) {
      console.error('Could not generate order link');
      return;
    }

    // Build URL to /app endpoint
    // Development: http://localhost:8090/app?...
    // Production: https://feydom.com/app?...
    const baseUrl = this.apiBaseUrl || 'https://feydom.com';
    let url = `${baseUrl}/app?${orderLink}`;

    // Add country if it's a specific country (not GLOBAL)
    if (this.countryCode && this.countryCode !== 'GLOBAL') {
      url += `&country=${this.countryCode}`;
    }
    // WKWebView swallows window.open, so on native this goes through an in-app browser.
    openExternal(url);
  }

  /**
   * Check if cart has any orderable items (from cart-enabled collections)
   * @returns {boolean}
   */
  hasItems() {
    const furnitureManager = this.app.getFurnitureManager();
    if (!furnitureManager) return false;
    return furnitureManager.getAllFurniture().some(f =>
      furnitureManager.isCartEnabledForFurniture(f.furnitureData)
    );
  }
}
